import { useState, useEffect } from "react";
import { DatePicker, Flex, Spin } from "antd";
import Title from "antd/es/typography/Title";
import dayjs from "dayjs";

import { useGetReports } from "./hooks/useReports";

import SalesOverview from "./components/SalesOverview";
import ExpensesOverview from "./components/ExpensesOverview";
import FinancialOverview from "./components/FinancialOverview";
import { ExpenseType } from "@/types/expenseType";
import {
  useGetLongestCharacters,
  useGetMinCardWidth,
} from "@/pages/Reports/hooks/useCardWidth";
import { useNavigate } from "react-router-dom";
import { useDateRangeStore } from "./store/useDateRange";

const Reports = () => {
  const navigate = useNavigate();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasUserChangedDates, setHasUserChangedDates] = useState(false);

  // Store for date range - persists until user logs out
  const { startDate, endDate, setDateRange } = useDateRangeStore();

  // Only fetch filtered data when user has changed dates and we have valid date range
  const shouldFetchFilteredData =
    hasUserChangedDates && !!startDate && !!endDate;

  // Initial fetch without date params to get default period (only once)
  const { data: initialReports, isLoading: isInitialLoading } = useGetReports({
    config: {
      params: {},
    },
  });

  // Fetch reports with selected date range (only when user changes dates)
  const { data: reports, isLoading: isReportsLoading } = useGetReports({
    config: {
      params: {
        date_from: startDate?.startOf("day").toISOString(),
        date_to: endDate?.endOf("day").toISOString(),
      },
    },
    enabled: shouldFetchFilteredData,
  });

  // Use backend's initial date range when first loaded
  useEffect(() => {
    if (startDate || endDate) {
      setHasUserChangedDates(true);
      setIsInitialLoad(false);
    } else if (initialReports && isInitialLoad) {
      const backendStartDate = dayjs(initialReports.period_from);
      const backendEndDate = dayjs(initialReports.period_to);

      setDateRange(backendStartDate, backendEndDate);
      setIsInitialLoad(false);
    }
  }, [initialReports, isInitialLoad, startDate, endDate, setDateRange]);

  // Handle date picker change
  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dayjs(dates[0]), dayjs(dates[1]));
      setHasUserChangedDates(true);
    }
  };

  // Handle open Expense Groups
  const handleOpenExpenseGroups = (
    expenseType: ExpenseType,
    startDate?: string,
    endDate?: string
  ) => {
    navigate(`/overview/expense-groups`, {
      state: {
        expenseType,
        startDate,
        endDate,
      },
    });
  };

  // Use initial reports for first render, then use filtered reports only when user changes dates
  const displayReports = hasUserChangedDates ? reports : initialReports;
  const isLoading =
    isInitialLoading || (isReportsLoading && hasUserChangedDates);

  // Get the longest characters and the minimum card width
  const longestCharacters = useGetLongestCharacters(displayReports);
  const minCardWidth = useGetMinCardWidth(longestCharacters);

  return (
    <>
      <Flex justify="space-between" align="start">
        <Title level={2} style={{ marginBottom: 24 }}>
          Overview
        </Title>
        <DatePicker.RangePicker
          value={[startDate, endDate]}
          onChange={handleDateChange}
          style={{ marginTop: 6 }}
          disabled={isInitialLoading}
        />
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" style={{ minHeight: 200 }}>
          <Spin size="large" />
        </Flex>
      ) : (
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`,
          }}
        >
          <FinancialOverview
            totalSales={displayReports?.sales?.final_amount}
            totalExpenses={displayReports?.expenses?.total_amount}
          />
          <SalesOverview sales={displayReports?.sales} />
          <ExpensesOverview
            expenses={displayReports?.expenses}
            totalSales={displayReports?.sales?.final_amount}
            onExpenseTypeSelect={(expenseType) =>
              handleOpenExpenseGroups(
                expenseType,
                startDate?.startOf("day").toISOString(),
                endDate?.endOf("day").toISOString()
              )
            }
          />
        </div>
      )}
    </>
  );
};

export default Reports;
